import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { COURSE_TYPES, LANGUAGES, NOTIFICATION_TYPES, ROUTES } from 'constants/index';
import { useAppContext } from 'services/context';
import { useTranslation } from 'services/hooks';
import { saveArrayAsCSV } from 'services/utils';
import Button from 'components/Button';
import Container from 'components/Container';
import Table from 'pages/ManagementPanel/Table';
import Select from 'components/Input/Select';
import Modal from 'components/Modal';

import Input from 'components/Input';
import Icon from 'components/Icon';
import { Checkbox } from 'components/Input/MultiSelect';
import './style.css';

// render checkbox input for the active column in the table
const ActiveCheckbox = ({ isActive, openModal, courseId, languageId }) => {
	return <input type="checkbox" name="active" id={courseId} languageid={languageId} checked={isActive} onChange={openModal} />;
};

const AdvancedCoursesManagement = () => {
	const { Api, addNotification } = useAppContext();

	const [ddls, setDdls] = useState([]);
	const [coursesWithActiveSubtitle, setCoursesWithActiveSubtitle] = useState([]);
	const [fileErrorMsg, setFileErrorMsg] = useState('');
	const [isVisible, setIsVisible] = useState(false);
	const [allReviews, setAllReviews] = useState([]);
	const [currentUserReviews, setCurrentUserReviews] = useState(undefined);
	const [updateCourse, setUpdateCourse] = useState(null);
	const [files, setFiles] = useState([]);

	const [readyToStartUsers, setReadyToStartUsers] = useState([]);
	const [readyToStartSelectedStudent, setReadyToStartSelectedStudent] = useState({});
	const [readyToStartOriginalData, setReadyToStartOriginalData] = useState({});

	const [subtitlesVisible, setSubtitlesVisible] = useState(false);
	const [reviewsVisible, setReviewsVisible] = useState(false);

	const [readyToStartFilter, setReadyToStartFilter] = useState(0);

	const [reviewsDirtyRows, setReviewsDirtyRows] = useState({});

	const formRef = useRef();
	const updateTeacherRef = useRef();
	//Stores all reviews that are currently being updated (before clicking the "Save changes" button).
	const editingReviewsIds = useRef({ showInCarousel: new Set(), showInList: new Set() });

	const navigate = useNavigate();
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'advanced_courses_management';

	const memoizedReviews = useMemo(() => {
		try {
			const reducedData = allReviews.reduce((acc, obj) => {
				const existingUser = acc.find(item => item.userName === obj.userName && item.courseName === obj.courseName);

				if (existingUser) {
					// user already exists → just push another review
					existingUser.reviewObj.push({ ...obj });
				} else {
					// first time we see this user → create base object
					acc.push({
						userName: obj.userName,
						questionnaireId: obj.questionnaireId,
						showInCarousel: obj.showInCarousel,
						showInList: obj.showInList,
						courseName: obj.courseName,
						reviewObj: [{ ...obj }],
					});
				}

				return acc;
			}, []);

			return reducedData;
		} catch (ex) {
			console.error(ex);
			return [];
		}
	}, [allReviews]);

	//upadte
	const updateCurrentUserReviews = useCallback(
		currentUser => {
			const _userReviews = memoizedReviews.find(({ userName }) => userName === currentUser);
			setCurrentUserReviews(_userReviews);
		},
		[allReviews, memoizedReviews],
	);

	const formCourseNamesOptions = useMemo(() => ddls.courses, [ddls]);
	const formLanguageOptions = useMemo(() => {
		// all languages options
		return LANGUAGES.map(lang => {
			return { value: lang.id, description: lang.description };
		});
	}, [LANGUAGES]);

	const formDataColumns = useMemo(() => {
		return [
			{
				Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_title`),
				columns: [
					{
						//running number - rows counter
						Header: '#',
						Cell: row => <>{row.row.index + 1}</>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_course_column`),
						accessor: 'courseName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_language_column`),
						accessor: 'languageName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_active_column`),
						accessor: 'isSubtitlesActive',
					},
				],
			},
		];
	}, [getTranslation]);

	//Sorting function for columns containing checkbox in the reviews table.
	const sortReviews = (rowA, rowB, columnId) => {
		let aChecked = !!rowA?.values[columnId];
		let bChecked = !!rowB?.values[columnId];

		const rowAState = reviewsDirtyRows[rowA?.original?.questionnaireId];
		const rowBState = reviewsDirtyRows[rowB?.original?.questionnaireId];
		const rowAHasChanges = !!rowAState && Object.values(rowAState).some(Boolean);
		const rowBHasChanges = !!rowBState && Object.values(rowBState).some(Boolean);

		//To ensure that the sort does not move the position of rows that are currently being updated.
		const _set = editingReviewsIds.current[columnId];
		if (rowAHasChanges && _set?.has(rowA?.original?.questionnaireId)) aChecked = !aChecked;
		else if (rowBHasChanges && _set?.has(rowB?.original?.questionnaireId)) bChecked = !bChecked;

		return bChecked - aChecked;
	};

	const addEditingReviewsId = (rowId, col) => {
		if (!col || !rowId) return;
		editingReviewsIds?.current[col]?.add(rowId);
	};

	const removeEditingReviewsId = rowId => {
		if (!rowId) return;

		const _obj = editingReviewsIds?.current;

		Object.keys(_obj).forEach(col => {
			const _set = _obj[col];
			_set?.delete(rowId);
		});
	};

	const handleCheckboxChange = (target, questionnaireId, key) => {
		if (!questionnaireId || !key) return;

		const checked = target?.checked;

		setReviewsDirtyRows(prev => {
			const prevRow = prev[questionnaireId] || {};

			return {
				...prev,
				[questionnaireId]: {
					...prevRow,
					[key]: !prevRow[key],
				},
			};
		});

		setAllReviews(prev => prev.map(review => (review.questionnaireId === questionnaireId ? { ...review, [key]: checked } : review)));
	};

	const handleSaveReview = async questionnaireId => {
		if (!questionnaireId) return;
		const rowToSave = memoizedReviews.find(r => r.questionnaireId === questionnaireId);
		if (!rowToSave) return;

		const res = await Api.UpdateReviewDisplayMode({
			questionnaireId,
			showInCarousel: rowToSave.showInCarousel,
			showInList: rowToSave.showInList,
		});
		if (res && !res.updated) {
			addNotification(getTranslation(TRANSLATION_KEY, res.updateReviewsResponseTranslationKey), NOTIFICATION_TYPES.ERROR, 8000);
			return;
		}
		if (res.updated && res.reviewsUpdated) {
			setAllReviews(prev =>
				prev.map(row => {
					const updatedRow = res?.reviewsUpdated?.find(u => u.questionnaireId === row.questionnaireId);
					return updatedRow ? { ...row, ...updatedRow } : row;
				}),
			);
			setReviewsDirtyRows(prev => {
				const { [questionnaireId]: _removed, ...rest } = prev; //removes the saved row from the ReviewdirtyRows
				return rest;
			});
		}

		removeEditingReviewsId(questionnaireId);

		addNotification(getTranslation(TRANSLATION_KEY, res.updateReviewsResponseTranslationKey), NOTIFICATION_TYPES.SUCCESS, 8000);
	};

	const reviewsDataColumns = useMemo(() => {
		return [
			{
				Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_title`),
				columns: [
					{
						Header: '#',
						Cell: row => <span>{row.row.index + 1}</span>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_user_name_column`),
						accessor: 'userName',
						Cell: ({ value }) => <div onClick={() => updateCurrentUserReviews(value)}>{value}</div>,
					},

					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_course_column`),
						accessor: 'courseName',
						Cell: ({ value }) => {
							return <div>{value}</div>;
						},
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_show_in_carousel_column`),
						accessor: 'showInCarousel',
						sortType: (rowA, rowB, columnId) => sortReviews(rowA, rowB, columnId),
						Cell: ({ row, value }) => {
							return (
								<Checkbox
									name="showInCarousel"
									onChange={({ target }) => handleCheckboxChange(target, row?.original?.questionnaireId, 'showInCarousel')}
									onMouseDown={() => addEditingReviewsId(row?.original?.questionnaireId, 'showInCarousel')}
									checked={value || false}
								/>
							);
						},
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_show_in_list_column`),
						accessor: 'showInList',
						sortType: (rowA, rowB, columnId) => sortReviews(rowA, rowB, columnId),
						Cell: ({ row, value }) => {
							return (
								<Checkbox
									name="showInList"
									onChange={({ target }) => handleCheckboxChange(target, row?.original?.questionnaireId, 'showInList')}
									onMouseDown={() => addEditingReviewsId(row?.original?.questionnaireId, 'showInList')}
									checked={value || false}
								/>
							);
						},
					},
					{
						id: 'SaveChanges',
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_save_changes_column`),
						disableSortBy: true,
						Cell: ({ row }) => {
							const rowState = reviewsDirtyRows[row.original.questionnaireId];
							const hasChanges = !!rowState && Object.values(rowState).some(Boolean);

							return (
								<Button
									small
									title={
										hasChanges
											? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_save_changes_column_title`)
											: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_no_changes_column_title`)
									}
									disabled={!hasChanges}
									extraClassName="save_review_state"
									onClick={() => {
										if (hasChanges) {
											handleSaveReview(row.original.questionnaireId);
										}
									}}
								/>
							);
						},
					},
				],
			},
		];
	}, [memoizedReviews, getTranslation]);

	const ReviewsCarouselCount = useMemo(() => {
		return memoizedReviews?.filter(review => review.showInCarousel).length || 0;
	}, [memoizedReviews]);

	const ReviewsListCount = useMemo(() => {
		return memoizedReviews?.filter(review => review.showInList).length || 0;
	}, [memoizedReviews]);

	const currentUserReviewsDataColumns = useMemo(() => {
		return [
			{
				Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_title`),
				columns: [
					{
						Header: '#',
						Cell: row => <>{row.row.index + 1}</>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_user_name_column`),
						accessor: 'userName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_name_column`),
						accessor: row => `${row?.fname}   ${row?.lname} `,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_question_column`),
						accessor: 'questionText',
						// contains html tags <br/>
						Cell: row => <span dangerouslySetInnerHTML={{ __html: row?.value }}></span>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_answer_column`),
						accessor: 'answerValue',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_class_column`),
						accessor: 'className',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_course_column`),
						accessor: 'courseName',
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_date_column`),
						accessor: 'dateCreated',
						Cell: row => <span>{new Date(row?.value || '').toLocaleString()}</span>,
					},
				],
			},
		];
	}, [getTranslation]);

	//extract readyToStart section update teacher ddls by selected user courseId
	const teacherOptions = useMemo(() => {
		if (!readyToStartSelectedStudent?.courseId) return ddls.courseTeachers;
		const options = ddls?.courseTeachers?.options?.filter(option => option.courseId === parseInt(readyToStartSelectedStudent.courseId));
		return { ...ddls.courseTeachers, options };
	}, [ddls, readyToStartSelectedStudent]);

	const fetchReviews = async () => {
		try {
			setReviewsDirtyRows({});
			const { courseReviews } = await Api.GetAllReviews();
			if (!courseReviews || !courseReviews.length) return;
			setAllReviews(courseReviews);
		} catch (error) {
			console.log('error', error);
		}
	};

	const fetchReadyToStartUsers = async () => {
		const _readyToStartUsers = await Api.GetMyCourseApplicantList();
		if (_readyToStartUsers) {
			setReadyToStartUsers(_readyToStartUsers);
		}
	};
	const fetchSubtitles = async () => {
		try {
			// all courses list
			const _ddls = await Api.GetAllManagementDDLS();
			if (_ddls) setDdls(_ddls);
			// only courses with subtitles
			let _coursesWithActiveSubtitle = await Api.GetCoursesWithActiveSubtitles();
			_coursesWithActiveSubtitle = _coursesWithActiveSubtitle.map(course => {
				const currLanguageName = LANGUAGES[course.languageId - 1].description;
				const isActive = course.isSubtitlesActive;
				const courseId = course.courseId;
				return {
					...course,
					languageName: currLanguageName,
					isActive,
					isSubtitlesActive: (
						<ActiveCheckbox isActive={isActive} openModal={openModal} courseId={courseId} languageId={course.languageId} />
					),
				};
			});
			setCoursesWithActiveSubtitle(_coursesWithActiveSubtitle);
		} catch (error) {
			console.error("Can't load the data", error);
		}
	};
	useEffect(() => {
		fetchSubtitles();
		fetchReviews();
		//init get users for Ready to Start section
		fetchReadyToStartUsers();
	}, []);

	// update the active of the subtitles in specific course
	const openModal = ({ target }) => {
		const courseId = parseInt(target.id);
		const languageId = parseInt(target.getAttribute('languageid'));
		setUpdateCourse({ courseId, languageId });
		setIsVisible(true);
	};

	// check the user's answer select and remove/not remove the subtitles accordingly to his answer
	const updateCourseWithSubtitle = async () => {
		setIsVisible(false);

		try {
			// find the specific course and add all the necessary keys for the backend
			const course = coursesWithActiveSubtitle.find(
				currCourse => currCourse.courseId == updateCourse.courseId && currCourse.languageId == updateCourse.languageId,
			);
			course.isActive = !course.isActive;
			course.updateSubsActivity = true;

			const res = await Api.UpdateCoursesWithActiveSubtitles(course);
			if (!res) {
				// the request failed

				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_update_error_message`),
					NOTIFICATION_TYPES.ERROR,
					8000,
				);

				return;
			}

			// The request success
			addNotification(
				getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_update_success_message`),
				NOTIFICATION_TYPES.SUCCESS,
				8000,
			);

			// update the local state
			delete course.updateSubsActivity;
			const courses = coursesWithActiveSubtitle.filter(currCourse => {
				if (currCourse.courseId == updateCourse.courseId && currCourse.languageId == updateCourse.languageId) {
					course.isSubtitlesActive = (
						<ActiveCheckbox
							isActive={course.isActive}
							openModal={openModal}
							courseId={currCourse.courseId}
							languageId={currCourse.languageId}
						/>
					);
					return course;
				}
				return currCourse;
			});
			setCoursesWithActiveSubtitle(courses);
		} catch (error) {
			console.error("Can't update this course", error);
		}
	};

	// add a new subtitles to a specific course
	const handleSubmitForm = async ev => {
		ev.preventDefault();

		const form = new FormData(ev.target);
		const formData = Object.fromEntries(form.entries());
		formData.isActive = true;

		try {
			// change the file's names to the correct format: Session_1_subtitles_EN.vtt
			const _files = files.map(file => {
				let fileName = file.name;
				fileName = fileName.substring(0, fileName.length - 4);
				fileName += `_subtitles_${LANGUAGES[formData.languageId - 1]?.iso}.vtt`;
				const _file = new File([file], fileName);
				return _file;
			});

			const res = await Api.UpdateCoursesWithActiveSubtitles({
				...formData,
				files: _files,
			});
			if (!res) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_insert_error_message`),
					NOTIFICATION_TYPES.ERROR,
					8000,
				);
				return;
			}

			addNotification(
				getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_insert_success_message`),
				NOTIFICATION_TYPES.SUCCESS,
				8000,
			);

			// reset form details
			formRef.current?.reset();
			setFiles([]);

			// if the course already exists in the local state
			const courseData = coursesWithActiveSubtitle.find(course => {
				return course.courseId == formData.courseId && course.languageId == formData.languageId;
			});

			if (courseData) {
				// check isActive status
				if (courseData.isActive) return;
				else {
					courseData.isActive = true;
					courseData.isSubtitlesActive = (
						<ActiveCheckbox isActive={true} openModal={openModal} courseId={courseData.courseId} languageId={courseData.languageId} />
					);
					const _updateCourses = coursesWithActiveSubtitle.filter(currCourse =>
						currCourse.courseId == formData.courseId && currCourse.languageId == formData.languageId ? courseData : currCourse,
					);
					setCoursesWithActiveSubtitle([..._updateCourses]);
					return;
				}
			}

			// add the new subtitle course to the local state

			const newSubtitleActiveCourse = { ...formData };
			newSubtitleActiveCourse.isSubtitlesActive = (
				<ActiveCheckbox isActive={formData.isActive} openModal={openModal} courseId={formData.courseId} languageId={formData.languageId} />
			);
			newSubtitleActiveCourse.languageName = LANGUAGES[formData.languageId - 1].description;
			newSubtitleActiveCourse.courseName = ddls.courses.options.find(course => course.value == formData.courseId)?.description;
			delete newSubtitleActiveCourse.files;

			const _coursesWithActiveSubtitle = [...coursesWithActiveSubtitle];
			_coursesWithActiveSubtitle.push(newSubtitleActiveCourse);
			setCoursesWithActiveSubtitle([..._coursesWithActiveSubtitle]);
		} catch (error) {
			console.error("Can't submit form", error);
		}
	};

	// upload files after validation to the type of the file
	const _handleUploadFile = async ({ target }) => {
		setFileErrorMsg('');
		const _files = [...target.files];

		_files.forEach(file => {
			const fileName = file.name;
			const fileType = fileName.slice(fileName.length - 3).toLowerCase();

			// check the file type
			if (fileType != 'vtt') {
				setFileErrorMsg(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_file_error_message`));
				return;
			}
		});

		setFiles(_files);
	};

	//Handles rejection of update details
	const _handleRejectUpdateDetailsRequest = async e => {
		e.preventDefault();
		const { userId, recordId } = readyToStartSelectedStudent;
		const _rejectRes = await Api.UpdateMyCourseFormStatus({
			userId,
			recordId,
			isRejected: true,
		});

		if (!_rejectRes.isUpdated) {
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_delete_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
			return;
		}

		setReadyToStartSelectedStudent([]); //Reset data.
		fetchReadyToStartUsers(); //Get the updated "Ready to Start" user list.
	};

	//TABLES COLUMNS AND HEADERS
	const readyToStartUsersColumns = useMemo(() => {
		return [
			{
				Header: ' ',
				columns: [
					{
						//running number - rows counter
						Header: '#',
						// accessor: 'userId',
						Cell: row => <>{row.row.index + 1}</>,
					},
					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_email_column`),
						accessor: 'email',
						Cell: row => <a href={`mailto:${row.value}`}>{row.value}</a>,
					},

					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_course_column`),
						accessor: 'courseName',
					},

					{
						Header: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_treat_column`),
						Cell: ({ row }) => (
							<span className="treat" onClick={() => _handleReadyToStartSelectStudent(row)}>
								<Icon icon="editIcon" />
							</span>
						),
					},
				],
			},
		];
	}, [getTranslation]);
	//filter the list by the courseType -> used for the ready to start DDL
	const filteredReadyToStartUsers = useMemo(() => {
		return (readyToStartFilter !== 0 && readyToStartUsers.filter(u => u.courseTypeID === readyToStartFilter)) || readyToStartUsers;
	}, [readyToStartUsers, readyToStartFilter]);

	//handles update user teacher feature in Ready to Start section
	const _handleUpdateTeacher = async e => {
		try {
			e?.preventDefault();
			const form = new FormData(e.target);
			const teacherAndUser = Object.fromEntries(form.entries());
			teacherAndUser.recordId = readyToStartSelectedStudent.recordId;
			if (!teacherAndUser.teacherId || !teacherAndUser.recordId) {
				addNotification(
					getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_update_validation_message`),
					NOTIFICATION_TYPES.ERROR,
					8000,
				);

				return;
			}
			const updated = await Api.UpdateStudentCourseTeacher(teacherAndUser);
			if (!updated) {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_update_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
				return;
			}
			const updatedUser = { ...readyToStartSelectedStudent, teacherId: parseInt(teacherAndUser.teacherId) };
			setReadyToStartSelectedStudent(updatedUser);
			const statusUpdate = await Api.UpdateMyCourseFormStatus(readyToStartSelectedStudent);
			if (!statusUpdate.isUpdated) {
				addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_update_error_message`), NOTIFICATION_TYPES.ERROR, 8000);
			}
			const updatedUsers = await Api.GetMyCourseApplicantList();
			if (updatedUsers) {
				setReadyToStartUsers(updatedUsers);
				setReadyToStartSelectedStudent({});
			}
			addNotification(getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_update_success_message`), NOTIFICATION_TYPES.SUCCESS, 8000);
		} catch (error) {
			console.warn(error);
		}
	};

	//handles Ready to Start table user onClick, also fetches the original user data before update teacher for user
	const _handleReadyToStartSelectStudent = async row => {
		//resets update teacher select value between user changes
		if (updateTeacherRef.current) {
			updateTeacherRef.current.value = '';
		}
		setReadyToStartSelectedStudent(row.original);
		if (row.original.userId) {
			const _userId = row.original.userId || 0;
			const originalUserData = await Api.GetUserDetailsByUserId(_userId);
			if (originalUserData) setReadyToStartOriginalData(originalUserData);
		}
	};

	const _handleExportToExcel = () =>
		saveArrayAsCSV(
			'MyCourse-Ready_to_Start_results_' + new Date().toLocaleString(),
			readyToStartUsers.map(({ email, courseName }) => ({ email, courseName })), //Export just email and course name.
		);

	const courseTypeFilterOptions = [
		{ value: 0, description: 'Both' },
		{ value: COURSE_TYPES.ESL, description: 'ESL' },
		{ value: COURSE_TYPES.IELTS, description: 'IELTS' },
	];

	return (
		<section className="AdvancedCoursesManagement">
			<Container>
				<div className="AdvancedCoursesManagement__ready_to_start">
					<div className="AdvancedCoursesManagement__ready_to_start__title">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_title`)}
					</div>
					<div className="AdvancedCoursesManagement__ready_to_start__container">
						<div className="table_wrapper">
							{readyToStartUsers.length > 0 ? (
								<>
									<div className="table_wrapper__top">
										<Select options={courseTypeFilterOptions} onChange={e => setReadyToStartFilter(parseInt(e.target.value))} />
										<Button
											title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_excel_button`)}
											extraClassName="excel"
											small
											rectangle
											type="secondary"
											onClick={_handleExportToExcel}
										/>
									</div>
									<Table columns={readyToStartUsersColumns} data={filteredReadyToStartUsers} pageSizeProp={10} />
								</>
							) : (
								getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_no_data`)
							)}
						</div>
						{readyToStartSelectedStudent && Object.keys(readyToStartSelectedStudent).length > 0 && (
							<div className="user_details">
								<div className="user_details__content">
									<div className="user_details__content__title">
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_title`)}
									</div>
									<div className="user_details__content__field">
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_course_field`)}{' '}
										<b>{readyToStartSelectedStudent.courseName}</b>
									</div>
									<div className="user_details__content__field">
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_email_field`)}{' '}
										<b>{readyToStartSelectedStudent.email}</b>
									</div>
									<div className="user_details__content__field">
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_first_name_field`)}{' '}
										<b>{readyToStartSelectedStudent.fname}</b>
									</div>
									<div className="user_details__content__field">
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_last_name_field`)}{' '}
										<b>{readyToStartSelectedStudent.lname}</b>
									</div>
									<div className="user_details__content__field">
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_phone_field`)}{' '}
										<b>{readyToStartOriginalData.phoneNumber}</b>
									</div>
									{readyToStartSelectedStudent.phoneNumber !== readyToStartOriginalData.phoneNumber && (
										<div className="user_details__content__field">
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_phone_update_request_field`)}{' '}
											<b>{readyToStartSelectedStudent.phoneNumber}</b>
										</div>
									)}
									<div className="user_details__content__field">
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_whatsapp_field`)}{' '}
										<b>{readyToStartSelectedStudent.isUsingWhatsApp ? 'Yes' : 'No'}</b>
									</div>
									<div className="user_details__content__field">
										{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_date_field`)}{' '}
										<b>
											{readyToStartSelectedStudent.dateCreated
												? new Date(readyToStartSelectedStudent.dateCreated || '').toLocaleString('en-GB', { dateStyle: 'short' })
												: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_not_provided_message`)}
										</b>
									</div>
									{Object.keys(readyToStartSelectedStudent)?.length > 0 &&
										readyToStartSelectedStudent.courseTypeID === COURSE_TYPES.ESL && (
											<div className="update_teacher">
												<form className="update_teacher__form" onSubmit={_handleUpdateTeacher}>
													<Select
														ref={updateTeacherRef}
														name={'teacherId'}
														placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_select_teacher`)}
														options={teacherOptions?.options || []}
														defaultValue=""
													/>
													<Button small title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_update_button`)} />
													{/* <Button small title="Cancel" onClick={() => setReadyToStartSelectedStudent({})} /> */}
													<Button
														small
														title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_user_details_delete_button`)}
														onClick={_handleRejectUpdateDetailsRequest}
													/>
												</form>
											</div>
										)}
									{Object.keys(readyToStartSelectedStudent)?.length > 0 &&
										readyToStartSelectedStudent.courseTypeID === COURSE_TYPES.IELTS && (
											<Button
												small
												title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ready_treat_column`)}
												extraClassName="treat_btn"
												onClick={_handleRejectUpdateDetailsRequest}
											/>
										)}
								</div>
							</div>
						)}
					</div>
				</div>
				<div className="AdvancedCoursesManagement__subtitles">
					<div className={`AdvancedCoursesManagement__subtitles__wrapper ${!subtitlesVisible ? 'close' : ''}`}>
						{subtitlesVisible ? (
							<>
								{/* section 1 - add subtitle form */}
								<div className="form__wrapper">
									<h1>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_form_title`)}</h1>
									<form onSubmit={handleSubmitForm} ref={formRef}>
										<Select
											label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_form_course_field`)}
											placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_form_course_field`)}
											name="courseId"
											options={formCourseNamesOptions?.options || []}
										/>
										<Select
											label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_form_language_field`)}
											placeHolder={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_form_language_field`)}
											name="languageId"
											options={formLanguageOptions || []}
										/>
										<label className={fileErrorMsg ? 'choose-file-label red' : 'choose-file-label'}>
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_form_file_field`)}
											<Input hidden id="file-uploader-input" type="file" multiple onChange={_handleUploadFile} />
										</label>
										<span className="file-format-msg">
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_form_file_format_message`)}
										</span>
										{fileErrorMsg && <div className="error-msg">{fileErrorMsg}</div>}
										{!!files.length && (
											<div>
												{files.map((file, idx) => (
													<div className="file-name" key={idx}>
														{file.name}
													</div>
												))}
											</div>
										)}
										<Button
											small
											title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_form_button`)}
											disabled={!files.length || fileErrorMsg}
										/>
									</form>
								</div>
								{/* section 2 - subtitle courses table */}
								<div className="table__wrapper">
									<h1>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_title`)}</h1>
									<Table columns={formDataColumns} data={coursesWithActiveSubtitle} />
								</div>
								<Icon icon="minusIcon" onClick={() => setSubtitlesVisible(prev => !prev)} />
							</>
						) : (
							<>
								<div className="AdvancedCoursesManagement__subtitles__wrapper__title">
									{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_wrapper_title`)}
								</div>
								<Icon icon="plusIcon" onClick={() => setSubtitlesVisible(prev => !prev)} />
							</>
						)}
					</div>
				</div>
				{/* Reviews */}
				<div className={`AdvancedCoursesManagement__reviews_wrapper ${!reviewsVisible ? 'close' : ''}`}>
					{reviewsVisible ? (
						<>
							{memoizedReviews && memoizedReviews.length > 0 && (
								<>
									<div className="AdvancedCoursesManagement__reviews_wrapper_head">
										<div className="AdvancedCoursesManagement__reviews_wrapper__title">
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_wrapper_title`)}
										</div>
										<Icon icon="minusIcon" onClick={() => setReviewsVisible(prev => !prev)} />
									</div>
									<div className="AdvancedCoursesManagement__reviews__state">
										<div className="carousel_count">
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_carousel_title`)}({ReviewsCarouselCount}/15)
										</div>
										<div className="list_count">
											{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_list_title`)}({ReviewsListCount})
										</div>
									</div>
									<Table columns={reviewsDataColumns} data={memoizedReviews} />
									<Button
										title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_button`)}
										small
										onClick={fetchReviews}
										extraClassName="refresh_reviews_button"
									/>
									<Modal
										isVisible={!!currentUserReviews}
										setVisibility={() => setCurrentUserReviews(undefined)}
										extraClassName="current_user_reviews"
									>
										<section className="modal_content">
											{currentUserReviews && (
												<Table columns={currentUserReviewsDataColumns} data={currentUserReviews.reviewObj} pageSizeProp={7} />
											)}
										</section>
									</Modal>
								</>
							)}
						</>
					) : (
						<>
							<div className="AdvancedCoursesManagement__reviews_wrapper__title">
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_reviews_wrapper_title`)}
							</div>
							<Icon icon="plusIcon" onClick={() => setReviewsVisible(prev => !prev)} />
						</>
					)}
				</div>
				<div className="AdvancedCoursesManagement__html_editor">
					<div className="AdvancedCoursesManagement__html_editor__title">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_html_editor_title`)}
					</div>
					<Icon icon="plusIcon" onClick={() => navigate(ROUTES.HTML_EDITOR)} />
				</div>
				{/* pop up before updating the active of specific course in the table */}
				<Modal isVisible={isVisible} hideCloseButton>
					<section className="modal_content">
						<div className="modal_title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_modal_title`)}</div>
						<div className="buttons_wrapper">
							<Button
								small
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_modal_yes_button`)}
								onClick={() => updateCourseWithSubtitle()}
							/>
							<Button
								small
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_subtitle_modal_no_button`)}
								type="outline"
								onClick={() => setIsVisible(false)}
							/>
						</div>
					</section>
				</Modal>
			</Container>
		</section>
	);
};

export default AdvancedCoursesManagement;
